import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

// El entrevistador: una persona del área de un cliente charla con la voz de
// GUIA, de a una pregunta. Atrás trabaja el equipo de agentes (ver
// netlify/functions/entrevistador.js). La pantalla es la misma noche del chat
// de la home: la pregunta grande en Playfair, lo que se toca en DM Sans.
//
// El navegador solo recuerda el id de la entrevista, para retomar si se
// recarga la página. La charla vive en el servidor.

const API = '/.netlify/functions/entrevistador';

// Las fases del director, dichas para la persona. Que vea dónde está parada
// (Nielsen, visibilidad del estado del sistema) sin la jerga del equipo.
const FASES = [
  { id: 'dimensionar', label: 'El área' },
  { id: 'mapear',      label: 'Cómo se hace' },
  { id: 'profundizar', label: 'Los detalles' },
  { id: 'cerrar',      label: 'Cierre' },
];

const guardarSesion = (area, sesion) => {
  try { localStorage.setItem(`guia-entrevista-${area}`, sesion); } catch {}
};
const leerSesion = (area) => {
  try { return localStorage.getItem(`guia-entrevista-${area}`); } catch { return null; }
};
const olvidarSesion = (area) => {
  try { localStorage.removeItem(`guia-entrevista-${area}`); } catch {}
};

const PasoAPaso = ({ fase }) => {
  const actual = Math.max(0, FASES.findIndex(f => f.id === fase));
  return (
    <div className="flex gap-4 items-center ui-sans text-[11px] tracking-wide">
      {FASES.map((f, i) => (
        <div key={f.id} className="flex items-center gap-1.5">
          <span
            className="rounded-full transition-all duration-700"
            style={{
              width: i === actual ? 18 : 6,
              height: 6,
              background: i <= actual ? '#29D880' : 'rgba(245,242,237,0.12)',
            }}
          />
          <span
            className="hidden sm:inline transition-colors duration-700"
            style={{ color: i === actual ? 'rgba(245,242,237,0.6)' : 'rgba(245,242,237,0.2)' }}
          >
            {f.label}
          </span>
        </div>
      ))}
    </div>
  );
};

const Pensando = () => (
  <div className="flex items-center gap-2" aria-label="Pensando">
    {[0, 1, 2].map(i => (
      <span
        key={i}
        className="w-1.5 h-1.5 rounded-full bg-guia-mint"
        style={{ animation: `pensar 1.6s ease-in-out ${i * 0.22}s infinite` }}
      />
    ))}
  </div>
);

const Respuesta = ({ onEnviar, disabled, inicial }) => {
  const [val, setVal] = useState(inicial || '');
  const ref = useRef(null);

  useEffect(() => {
    if (!disabled && ref.current) ref.current.focus();
  }, [disabled]);

  // Crece con el texto, hasta un tope.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 220)}px`;
  }, [val]);

  const enviar = () => {
    if (!val.trim() || disabled) return;
    onEnviar(val.trim());
  };

  return (
    <div className="w-full max-w-2xl mx-auto" style={{ animation: 'fadeSlideUp 0.5s ease-out 0.1s both' }}>
      <div className="flex gap-3 items-end">
        <textarea
          ref={ref}
          rows={1}
          value={val}
          maxLength={4000}
          onChange={e => setVal(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); }
          }}
          placeholder="Contame…"
          disabled={disabled}
          className="flex-1 resize-none bg-transparent border-b border-[#F5F2ED]/15 focus:border-guia-mint
                     px-0 py-2 text-base text-[#F5F2ED] placeholder-[#F5F2ED]/25
                     outline-none transition-colors duration-200 disabled:opacity-40"
        />
        <button
          onClick={enviar}
          disabled={disabled || !val.trim()}
          aria-label="Enviar"
          className="text-guia-mint text-xl pb-2 hover:scale-110 transition-transform
                     disabled:opacity-20 disabled:cursor-not-allowed"
        >
          →
        </button>
      </div>
      <p className="ui-sans text-[11px] text-[#F5F2ED]/20 mt-2 tracking-wide">
        Enter para enviar · Shift + Enter para otra línea
      </p>
    </div>
  );
};

const Entrevista = () => {
  const { area } = useParams();
  const [info, setInfo]           = useState(null);   // { empresa, area, minutos }
  const [etapa, setEtapa]         = useState('cargando'); // cargando | inexistente | entrada | charla | fin
  const [sesion, setSesion]       = useState(null);
  const [texto, setTexto]         = useState('');
  const [escribiendo, setEscribiendo] = useState(false);
  const [esperando, setEsperando] = useState(false);
  const [fase, setFase]           = useState('dimensionar');
  const [error, setError]         = useState(null);   // { respuesta } para reintentar
  const [visible, setVisible]     = useState(true);
  const typingRef = useRef(null);

  // Palabra por palabra, al mismo ritmo que el chat de la home.
  const escribir = (t, alTerminar) => {
    const partes = t.split(' ');
    let i = 0;
    setTexto('');
    setEscribiendo(true);
    clearInterval(typingRef.current);
    typingRef.current = setInterval(() => {
      i++;
      setTexto(partes.slice(0, i).join(' '));
      if (i >= partes.length) {
        clearInterval(typingRef.current);
        setEscribiendo(false);
        alTerminar && alTerminar();
      }
    }, 115);
  };

  useEffect(() => () => clearInterval(typingRef.current), []);

  useEffect(() => {
    fetch(`${API}?area=${encodeURIComponent(area)}`)
      .then(r => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(d => { setInfo(d); setEtapa('entrada'); })
      .catch(() => setEtapa('inexistente'));
  }, [area]);

  const turno = async (cuerpo, respuesta) => {
    setError(null);
    setEsperando(true);
    setVisible(false);
    try {
      const r = await fetch(API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ area, ...cuerpo }),
      });
      // La entrevista guardada ya no existe: se arranca una nueva.
      if (r.status === 404 && cuerpo.sesion && !respuesta) {
        olvidarSesion(area);
        return turno({}, null);
      }
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'error');

      setSesion(d.sesion);
      guardarSesion(area, d.sesion);
      if (d.fase) setFase(d.fase);
      setEsperando(false);
      setVisible(true);
      escribir(d.pregunta, () => {
        if (d.fin) {
          olvidarSesion(area);
          setTimeout(() => setEtapa('fin'), 2400);
        }
      });
    } catch {
      setEsperando(false);
      setVisible(true);
      setError({ respuesta });
    }
  };

  const empezar = () => {
    setEtapa('charla');
    const previa = leerSesion(area);
    turno(previa ? { sesion: previa } : {}, null);
  };

  const responder = (respuesta) => turno({ sesion, respuesta }, respuesta);

  const reintentar = () => {
    if (!sesion) return empezar();
    turno(error?.respuesta ? { sesion, respuesta: error.respuesta } : { sesion }, error?.respuesta);
  };

  const puedeResponder = etapa === 'charla' && !esperando && !escribiendo && !error && texto;

  return (
    <div className="guia-noche min-h-screen flex flex-col ui-sans">
      <header className="relative z-10 flex items-center justify-between px-6 md:px-12 py-5">
        <span className="text-[#F5F2ED]/55 font-semibold tracking-[0.2em] text-sm">GuIA</span>
        {etapa === 'charla' && <PasoAPaso fase={fase} />}
      </header>

      <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 md:px-16 pb-10">
        {etapa === 'cargando' && <Pensando />}

        {etapa === 'inexistente' && (
          <div className="text-center max-w-md" style={{ animation: 'fadeSlideUp 0.8s ease-out both' }}>
            <p className="voz-ente text-3xl md:text-4xl text-[#F5F2ED]">Este link no está abierto.</p>
            <p className="text-[#F5F2ED]/45 text-sm mt-5 leading-relaxed">
              Puede que la entrevista ya se haya cerrado. Si te lo pasaron hace poco, pedí que te lo manden de nuevo.
            </p>
          </div>
        )}

        {etapa === 'entrada' && info && (
          <div className="flex flex-col items-center text-center gap-8 max-w-xl">
            <div style={{ animation: 'fadeSlideUp 0.8s ease-out both' }}>
              <p className="text-[#F5F2ED]/40 text-xs tracking-[0.3em] uppercase mb-5">
                {info.empresa} · {info.area}
              </p>
              <h1 className="voz-ente text-4xl md:text-6xl text-[#F5F2ED] leading-tight">
                Contame cómo<br />trabajás.
              </h1>
              <p className="text-[#F5F2ED]/55 text-base md:text-lg font-light mt-6 leading-relaxed">
                Es una charla de unos {info.minutos} minutos, de a una pregunta.
                Lo que cuentes sirve para que An entienda cómo funciona el área, tal como es hoy.
                No hay respuestas correctas.
              </p>
            </div>
            <button
              onClick={empezar}
              className="px-9 py-4 rounded-full bg-guia-mint text-black font-semibold text-base
                         hover:bg-guia-mint/90 transition-all duration-200 hover:scale-105 active:scale-[0.98]"
              style={{ animation: 'fadeSlideUp 0.8s ease-out 0.2s both', boxShadow: '0 0 40px rgba(41,216,128,0.25)' }}
            >
              Empezar →
            </button>
            <p
              className="text-[#F5F2ED]/30 text-xs leading-relaxed max-w-sm"
              style={{ animation: 'fadeSlideUp 0.8s ease-out 0.35s both' }}
            >
              Te va a preguntar una inteligencia artificial que trabaja con An. No escribas contraseñas
              ni datos personales de clientes: alcanza con decir dónde están. Si cerrás la página, retomás donde quedaste.
            </p>
          </div>
        )}

        {etapa === 'charla' && (
          <div
            className="flex flex-col items-center text-center gap-10 w-full max-w-3xl"
            style={{
              opacity: visible ? 1 : 0,
              filter: visible ? 'blur(0px)' : 'blur(3px)',
              transform: visible ? 'translateY(0)' : 'translateY(12px)',
              transition: 'opacity 0.3s ease, transform 0.3s ease, filter 0.3s ease',
            }}
          >
            {esperando && <Pensando />}

            {!esperando && error && (
              <div className="flex flex-col items-center gap-5" style={{ animation: 'fadeSlideUp 0.5s ease-out both' }}>
                <p className="voz-ente text-2xl md:text-3xl text-[#F5F2ED]/80">
                  Se me cortó la conexión un segundo.
                </p>
                <p className="text-[#F5F2ED]/40 text-sm">Lo que escribiste no se perdió.</p>
                <button
                  onClick={reintentar}
                  className="px-6 py-3 rounded-full border border-[#F5F2ED]/15 text-[#F5F2ED]/70 text-sm
                             hover:border-guia-mint hover:text-guia-mint transition-all duration-300"
                >
                  Probar de nuevo
                </button>
              </div>
            )}

            {!esperando && !error && (
              <p className="voz-ente text-2xl md:text-4xl leading-snug whitespace-pre-line text-[#F5F2ED]">
                {texto}
                {escribiendo && texto && (
                  <span
                    className="inline-block w-0.5 h-7 md:h-9 bg-guia-mint ml-1 align-middle rounded-full"
                    style={{ animation: 'cursorBlink 0.9s step-end infinite' }}
                  />
                )}
              </p>
            )}

            {puedeResponder && (
              <Respuesta key={texto} onEnviar={responder} disabled={!puedeResponder} />
            )}
          </div>
        )}

        {etapa === 'fin' && (
          <div className="text-center max-w-lg" style={{ animation: 'fadeSlideUp 0.9s ease-out both' }}>
            <div className="w-8 h-px bg-guia-mint/50 mx-auto mb-8" />
            <p className="voz-ente text-4xl md:text-5xl text-[#F5F2ED]">Gracias.</p>
            <p className="text-[#F5F2ED]/50 text-base font-light mt-6 leading-relaxed">
              Ya quedó todo. An lo va a leer antes de juntarse con ustedes. Podés cerrar esta página.
            </p>
          </div>
        )}
      </main>

      <footer className="relative z-10 px-6 py-4 text-center">
        <span className="text-[#F5F2ED]/10 text-xs tracking-widest">guia-ai.com</span>
      </footer>
    </div>
  );
};

export default Entrevista;
